<template>
    <div v-if="configStore.personArray && tableStore.availableDates.length > 0">
        <TableFieldSet title="Tischreservierung">
          <div class="col-12 pt-0">
            <div class="text-sm" v-html="configStore.configObject.texts.welcomemessage"></div>
          </div>
            <form @submit.prevent="onSubmit">
                <div class="col-12 text-secondary small mb-2">
                  Hinweis: Tage mit einem Kreis sind Event-Tage. Reservierungen beziehen sich auf das Event an diesem Tag.
                </div>
                <div class="col-12">
                    <div class="formgrid grid">
                        <div class="col-12 sm:col mb-1 sm:w-20rem">
                            <TableDropDown id="persons"
                                           :all-options="configStore.personArray"
                                           :error-message="errors.persons" :value="persons"
                                           name="persons"
                                           placeholder="Wähle Personen" @update:modelValue="(val) => persons = val"/>
                        </div>
                        <div class="col-12 sm:col sm:w-20rem">
                            <TableCalendar id="calendar"
                                           :error-message="errors.calendar"
                                           :markers="tableStore.markers"
                                           :maxDate="maxDate"
                                           :minDate="minDate"
                                           :value="calendar"
                                           name="calendar"
                                           @datePicked="getTimes"/>
                        </div>
                    </div>
                </div>
                <!-- Event info boxes (appears when selected date has events) -->
                <div v-if="hasEventsOnSelectedDate" class="col-12">
                  <div v-if="eventsForSelectedDate.length > 1" class="text-secondary small mb-2">
                    {{ eventsForSelectedDate.length }} Events an diesem Tag verfügbar. Bitte wählen Sie ein Event:
                  </div>
                  <div v-for="(ev, idx) in eventsForSelectedDate" :key="ev.id || idx" 
                       class="a360-event-box d-flex align-items-center justify-content-between mb-2" 
                       role="button" tabindex="0" 
                       @click="onEventClick(ev)" 
                       @keydown.enter.prevent="onEventClick(ev)">
                    <div class="a360-event-text me-3">
                      <!-- Row 1: time - name -->
                      <div class="a360-event-row1 mb-1">
                        <span class="fw-semibold">{{ formatEventTime(ev.event_date) }} - {{ ev.name }}</span>
                      </div>
                      <!-- Row 2: description (smaller font) -->
                      <div v-if="ev.description" class="text-secondary small a360-event-row2">
                        {{ ev.description }}
                      </div>
                    </div>
                    <!-- Arrow aligned to the far right of the box -->
                    <i class="pi pi-angle-right a360-event-arrow flex-shrink-0"></i>
                  </div>
                </div>
                <div v-if="tableStore.availableTimes.length > 0 || fetching" class="col-12 flex align-content-center">
                    <TableListBox v-if="tableStore.availableTimes.length > 0" id="times"
                                  :all-options="tableStore.availableTimes" :error-message="errors.times" :value="times"
                                  name="times"
                                  styling="max-height:16rem" @update:modelValue="(val) => times = val"/>
                    <TableSpinner v-else-if="fetching" class="m-auto h-16rem"
                                  styling="width: 50px; height: 50px"></TableSpinner>
                </div>
                <div class="col-12">
                    <TableButton class="mr-auto" label="Weiter" type="submit"/>
                </div>
            </form>
        </TableFieldSet>
    </div>
  <ProgressSpinner v-else style="width: 80px; height: 80px" strokeWidth="8"
                   animationDuration=".5s" aria-label="Custom ProgressSpinner" />
</template>

<script lang="ts" setup>
import {computed, onMounted, ref, watch} from "vue";
import TableDropDown from "@/components/TableDropDown.vue";
import TableCalendar from "@/components/TableCalendar.vue";
import TableFieldSet from "@/components/TableFieldSet.vue";
import TableListBox from "@/components/TableListBox.vue";
import TableButton from "@/components/TableButton.vue";
import TableSpinner from "@/components/TableSpinner.vue";
import {useConfigStore} from "@/stores/ConfigStore";
import {useTableStore} from "@/stores/TableStore";
import router from "@/router";
import {useRoute} from 'vue-router'
import validation_messages from '../validation_messages.json';
import {date as yup_date, object, string} from "yup";
import {useForm} from "vee-validate";
import {useUserStore} from "@/stores/UserStore";
import ProgressSpinner from "primevue/progressspinner";
import {useToast} from "primevue/usetoast";

const route = useRoute();
const configStore = useConfigStore();
const tableStore = useTableStore();
const userStore = useUserStore();
const toast = useToast();

const fetching = ref(false);
const schema = object({
    persons: string().required(validation_messages.persons),
    calendar: yup_date().required(validation_messages.date),
    times: string().required(validation_messages.times),
});

const {defineField, handleSubmit, resetForm, errors, setFieldError} = useForm({
    validationSchema: schema,
});

const [persons] = defineField('persons');
const [calendar] = defineField('calendar');
const [times] = defineField('times');

const minDate = computed(() => {
  const [day, month, year] = tableStore.availableDates[0].date.split(".");
  return new Date(`${month}/${day}/${year}`);
})

const maxDate = computed(() => {
  let day, month, year;

  const lastDateEntry = tableStore.availableDates.at(-1)?.date;
  if (lastDateEntry) {
    [day, month, year] = lastDateEntry.split('.');
  }
  return new Date(`${month}/${day}/${year}`);
})

onMounted(async () => {
    await configStore.getConfigObject();
    await tableStore.getDays();
    await tableStore.getEventDays();
    if (userStore.session !== null) await userStore.getUserData();

  if (tableStore.chosenTable.time) { times.value = tableStore.chosenTable.time; }
  if (tableStore.chosenTable.persons) { persons.value = tableStore.chosenTable.persons; }

    // inject custom css from the store and append to head
    const styleSheet = document.createElement("style");
    styleSheet.innerText = configStore.configObject.customcss
    document.head.appendChild(styleSheet)

    if (!Array.isArray(tableStore.availableDates) || tableStore.availableDates.length <= 0) return;
    tableStore.availableDates.map((date: any) => {
        if (date.opened === 0) {
            tableStore.markers.push({
                date: new Date(date.date.split('.').reverse()),
                type: 'line',
                tooltip: [{text: date.reason ? date.reason : 'Restaurant wegen Feiertag geschlossen', color: ''}],
            })
        }
    });
})

async function getTimes(date: Date) {
    times.value = undefined;
    calendar.value = date;
    fetching.value = true;
    await tableStore.getTimes(date, persons.value);
    fetching.value = false;
}

watch(persons, async (newVal) => {
    if (calendar.value && newVal) {
        await getTimes(calendar.value);
    }
});

// Returns array of events for the selected date (or empty array)
const eventsForSelectedDate = computed(() => {
  if (!calendar.value) return [];
  try {
    const dateStr = (calendar.value as Date).toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'});
    // Pinia unwraps Refs returned from the store, so access the object directly (no .value here)
    const events = (tableStore as any).eventsByDate[dateStr];
    return Array.isArray(events) ? events : [];
  } catch (e) {
    return [];
  }
});

// For backward compatibility: returns true if there are events on the selected date
const hasEventsOnSelectedDate = computed(() => eventsForSelectedDate.value.length > 0);

function formatEventTime(unix?: number | null) {
  if (!unix) return '-';
  const d = new Date(unix * 1000);
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

async function onEventClick(ev: any) {
  if (!persons.value) {
    // mark persons dropdown as invalid and cancel
    setFieldError('persons', validation_messages.persons);
    return;
  }
  
  if (!ev) return;
  
  // Check availability for type 1 (simple tickets)
  if (ev.event_type === 1) {
    const availableSeats = ev.available_seats !== undefined ? ev.available_seats : ev.total_tickets;
    const requestedPersons = Number(persons.value) || 1;
    
    if (availableSeats <= 0) {
      toast.add({ 
        severity: 'error', 
        summary: 'Event ausgebucht', 
        detail: 'Dieses Event ist leider ausgebucht. Es sind keine Tickets mehr verfügbar.',
        life: 5000 
      });
      return;
    }
    
    if (requestedPersons > availableSeats) {
      toast.add({ 
        severity: 'warn', 
        summary: 'Nicht genügend Tickets', 
        detail: `Es sind nur noch ${availableSeats} Tickets verfügbar. Bitte reduzieren Sie die Personenzahl.`,
        life: 5000 
      });
      return;
    }
  }
  
  // For type 2 (table options), availability is checked on the contact page
  // since the user needs to select a specific table option there
  
  // Store selected event for later steps
  tableStore.selectedEvent = ev;

  // Mirror the "Weiter" behavior: persist selected values and navigate to contact
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  } as const;
  if (calendar.value) {
    tableStore.chosenTable.date = (calendar.value as Date).toLocaleString('de-DE', options);
  }
  if (persons.value) {
    tableStore.chosenTable.persons = persons.value as any;
  }
  // Keep any already chosen time; if none selected yet, leave as-is
  if (times.value) {
    tableStore.chosenTable.time = times.value as any;
  }

  await router.push({
    name: 'contact',
    query: {
      store: route.query.store as any,
    },
  });
}

const onSubmit = handleSubmit(async (values) => {
    const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    };

    tableStore.chosenTable.date = await values.calendar.toLocaleString("de-DE", options);
    tableStore.chosenTable.time = await values.times;
    tableStore.chosenTable.persons = await values.persons;

    await router.push({
        name: 'contact',
        query: {
            store: route.query.store,
        },
    })
});


</script>

<style scoped>
.a360-event-box {
  position: relative;
  border: 1px solid #ced4da;
  border-radius: 8px;
  padding: 12px 44px 12px 14px; /* extra right padding for arrow */
  background: #f8f9fa;
}
.a360-event-box:hover, .a360-event-box:focus {
  background: #eef3ff;
  outline: none;
}

/* right-aligned arrow pinned to middle right */
.a360-event-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.25rem; /* matches fs-4 */
  color: #6c757d; /* Bootstrap secondary */
}
</style>